/**
 * Long/Short position interaction, co-located with the tool (Phase 5). The
 * hairiest: placement (1:3 R:R default), the live preview, the full drag map
 * (body translate via snapshot, corner handles updating two axes, single-axis
 * line/edge resizes), and the per-position menu. The body-hit ROUTING that
 * decides which anchor a click grabs lives in positionRenderer.tsx; this is the
 * field math the chart's drag loop applies.
 */
import {
  removePosition,
  setPositionDialogId,
  type Position,
  type PositionType,
} from "@/state/slices/uiSlice";
import type { AppDispatch } from "@/state/store";
import type { DragTarget, MenuItem } from "@/components/Chart/types";
import type { Candle } from "@/api/types";
import { shiftTimeByLogical } from "@/tools/_shared/bodyDrag";

type Point = { time: number; price: number };

/** Entry = anchor 1, TP = anchor 2's price, SL placed for a default 1:3 R:R
 *  (one-third of the TP distance on the opposite side of entry). */
export function buildPosition(
  type: PositionType,
  a: Point,
  b: Point,
  tfSec: number,
): Position {
  const entry = a.price;
  const tp = b.price;
  const sl = entry - (tp - entry) / 3;
  const t1 = Math.min(a.time, b.time);
  const t2 = Math.max(a.time, b.time);
  const endTime = t2 > t1 ? t2 : t1 + 20 * tfSec;
  return {
    id: `pos_${Date.now()}`,
    type,
    entryPrice: entry,
    slPrice: sl,
    tpPrice: tp,
    startTime: t1,
    endTime,
  };
}

/** Live preview box while placing — mirrors buildPosition's 1:3 R:R. */
export function positionPreview(
  type: PositionType,
  anchor: Point,
  hover: Point,
): Position {
  return {
    id: "preview",
    type,
    entryPrice: anchor.price,
    tpPrice: hover.price,
    slPrice: anchor.price - (hover.price - anchor.price) / 3,
    startTime: Math.min(anchor.time, hover.time),
    endTime: Math.max(anchor.time, hover.time),
  };
}

/** Whole-position MOVE for one drag tick. Translates all five fields by the
 *  cursor-minus-mousedown delta. The two time fields shift through the shared
 *  `shiftTimeByLogical` (logical/bar space) so the box keeps its shape across
 *  chart gaps (weekends) — same gap-correct path the two-anchor tools use. Price
 *  is continuous, so it's a plain delta. Returns null when nothing applies. */
export function positionBodyDragPartial(
  drag: Extract<DragTarget, { type: "position"; anchor: "body" }>,
  currentLogical: number | null,
  price: number | null,
  candles: Candle[],
): Partial<Omit<Position, "id" | "type">> | null {
  if (currentLogical == null || price == null) return null;
  const dLogical = currentLogical - drag.initial.anchorLogical;
  const startTime = shiftTimeByLogical(drag.initial.startTime, dLogical, candles);
  const endTime = shiftTimeByLogical(drag.initial.endTime, dLogical, candles);
  if (startTime == null || endTime == null) return null;
  const dp = price - drag.initial.anchorPrice;
  return {
    startTime,
    endTime,
    entryPrice: drag.initial.entryPrice + dp,
    slPrice: drag.initial.slPrice + dp,
    tpPrice: drag.initial.tpPrice + dp,
  };
}

/** Field update for a non-body drag tick: corners update price + time edge;
 *  single handles update one axis. The whole-position MOVE lives in
 *  `positionBodyDragPartial` (logical-space, gap-correct). Returns null when
 *  nothing applies. */
export function positionDragPartial(
  drag: Exclude<Extract<DragTarget, { type: "position" }>, { anchor: "body" }>,
  time: number | null,
  price: number | null,
): Partial<Omit<Position, "id" | "type">> | null {
  const p: Partial<Omit<Position, "id" | "type">> = {};
  if (drag.anchor === "tp-right") {
    if (price != null) p.tpPrice = price;
    if (time != null) p.endTime = time;
  } else if (drag.anchor === "tp-left") {
    if (price != null) p.tpPrice = price;
    if (time != null) p.startTime = time;
  } else if (drag.anchor === "sl-right") {
    if (price != null) p.slPrice = price;
    if (time != null) p.endTime = time;
  } else if (drag.anchor === "sl-left") {
    if (price != null) p.slPrice = price;
    if (time != null) p.startTime = time;
  } else if (drag.anchor === "entry") {
    if (price != null) p.entryPrice = price;
  } else if (drag.anchor === "sl") {
    if (price != null) p.slPrice = price;
  } else if (drag.anchor === "tp") {
    if (price != null) p.tpPrice = price;
  } else if (drag.anchor === "left") {
    if (time != null) p.startTime = time;
  } else if (drag.anchor === "right") {
    if (time != null) p.endTime = time;
  }
  return Object.keys(p).length > 0 ? p : null;
}

/** Per-position right-click menu: open the settings dialog, or delete. */
export function positionMenuItems(
  dispatch: AppDispatch,
  tabId: string,
  posId: string,
): MenuItem[] {
  return [
    {
      label: "Edit settings",
      action: () => dispatch(setPositionDialogId(posId)),
    },
    {
      label: "Delete",
      action: () => dispatch(removePosition({ tabId, id: posId })),
      danger: true,
    },
  ];
}
